import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  Lock,
  X,
  MapPin,
  Layers,
  CheckCircle,
  Clock,
  Sparkles,
  FileText,
  Image as ImageIcon,
  ChevronRight,
  Eye,
  Home,
  ShieldCheck,
  Info,
} from 'lucide-react';
import { OutlineButton, PrimaryButton, CloseButton } from '../../components/Buttons';
import axiosInstance from '../../services/api/axios';
import { fetchProjectPhases, fetchPropertyConfigurations, fetchProjectPhaseMedia } from '../../services/projects';
import { buildPhaseDataItemFromProjectPhase } from '../../utils/buildInteriorPhasePayload';
import { toast } from 'react-toastify';
import { useUser } from '../../context/UserContext';
import { getMediaUrl, mapMediaItems, isDetailedFloorPlanMedia, normalizePropertyConfigMediaForUI } from '../../utils/media';
import { organizePhaseDocumentsFromMedia } from '../../utils/organizePhaseDocumentsFromMedia';
import { PhaseDocumentFileRow } from '../../components/PhaseDocumentFileRow';
import DeveloperProjectBoqTable from '../../components/DeveloperProjectBoqTable';
import LoadingImage from '../../components/LoadingImage';

// Tabs available on this page. Hash-driven so the URL reflects the active section.
const TAB_IDS = ['overview', 'configs', 'documents', 'boq'];
const parseTabFromHash = (hash) => {
  if (!hash) return 'overview';
  const cleaned = hash.replace(/^#/, '').toLowerCase();
  return TAB_IDS.includes(cleaned) ? cleaned : 'overview';
};

const InteriorDesignerPhaseDetails = () => {
  const { projectId, phaseId } = useParams();
  const navigate = useNavigate();
  const navLocation = useLocation();
  const { userInfo } = useUser();

  // Get phase data from navigation state if available
  const passedPhaseData = navLocation.state?.phaseData;
  const [phase, setPhase] = useState(null);
  const [project, setProject] = useState(null);
  const [configurations, setConfigurations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [phaseImage, setPhaseImage] = useState(null);
  const [configImages, setConfigImages] = useState({});
  const [phaseCompletionPercent, setPhaseCompletionPercent] = useState(0);
  const [subscriptionStatus, setSubscriptionStatus] = useState('none'); // 'none', 'pending', 'paid'
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [buildings, setBuildings] = useState([]);
  // /projects/buildings returns building basics; per-building completion %
  // lives on /building/metadata. Fetched separately and averaged as the
  // ultimate fallback for phase completion when no rolled-up % is provided.
  const [buildingMetadata, setBuildingMetadata] = useState([]);
  const [propertyCounts, setPropertyCounts] = useState({});
  const [phaseMedia, setPhaseMedia] = useState({ images: [], guidelines: [], boq: [] });
  const [subscriptionFee, setSubscriptionFee] = useState(0);
  // Designer's declared on-site (in-person) availability for THIS project phase.
  const [availableOnSite, setAvailableOnSite] = useState(false);
  const [boqModalOpen, setBoqModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(() =>
    typeof window !== 'undefined' ? parseTabFromHash(window.location.hash) : 'overview'
  );

  // Keep activeTab in sync with the URL hash (back/forward, deep links).
  useEffect(() => {
    setActiveTab(parseTabFromHash(navLocation.hash));
  }, [navLocation.hash]);

  const selectTab = (tabId) => {
    setActiveTab(tabId);
    if (typeof window !== 'undefined') {
      navigate(`${navLocation.pathname}#${tabId}`, { replace: true });
    }
  };

  // Image caching utilities
  const CACHE_TTL_MS = 4 * 60 * 1000; // 4 minutes

  const getCachedImage = (key) => {
    const cached = localStorage.getItem(`media_${key}`);
    if (!cached) return null;
    try {
      const parsed = JSON.parse(cached);
      if (!parsed || !parsed.data || !parsed.cachedAt) return null;
      const isStale = Date.now() - parsed.cachedAt > CACHE_TTL_MS;
      return isStale ? null : parsed.data;
    } catch (error) {
      console.error("error while parsing cached data:", error);
      return null;
    }
  };

  const setCachedImage = (key, mediaData) => {
    try {
      const payload = {
        data: mediaData,
        cachedAt: Date.now(),
      };
      localStorage.setItem(`media_${key}`, JSON.stringify(payload));
    } catch (error) {
      console.error("Error storing in cache:", error);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchPhaseDetails = async () => {
      try {
        setLoading(true);

        // Step 1: Use passed phase data if available, otherwise fetch
        let phaseDataItem = passedPhaseData;

        if (!phaseDataItem) {
          // GET /interior-designers/{designer_id}/phases — designer_id must be the logged-in designer's loginId
          try {
            const designerId = userInfo?.loginId;
            let phaseDataResponse = null;

            if (designerId) {
              try {
                phaseDataResponse = await axiosInstance.get(
                  `/interior-designers/${designerId}/phases`,
                  { params: { projectPhaseId: phaseId } }
                );
              } catch (designerErr) {
                console.warn(
                  'Interior-designer phases fetch failed; will try /projects/phase if needed',
                  designerErr
                );
              }
            }

            if (phaseDataResponse?.error || phaseDataResponse?.message) {
              console.error('API returned error:', phaseDataResponse.error || phaseDataResponse.message);
              phaseDataResponse = null;
            }

            if (phaseDataResponse) {
              if (Array.isArray(phaseDataResponse)) {
                phaseDataItem = phaseDataResponse.find(item =>
                  item.projectPhase?.projectPhaseId == phaseId
                ) || phaseDataResponse[0];
              } else if (phaseDataResponse.items && Array.isArray(phaseDataResponse.items)) {
                phaseDataItem = phaseDataResponse.items.find(item =>
                  item.projectPhase?.projectPhaseId == phaseId
                ) || phaseDataResponse.items[0];
              } else if (phaseDataResponse.projectPhase) {
                phaseDataItem = phaseDataResponse;
              }
            }

            if (!phaseDataItem?.projectPhase && projectId && phaseId) {
              const phaseResponse = await fetchProjectPhases({
                projectPhaseId: phaseId,
                projectId,
              });
              let phasesArray = [];
              if (phaseResponse?.items && Array.isArray(phaseResponse.items)) {
                phasesArray = phaseResponse.items;
              } else if (Array.isArray(phaseResponse)) {
                phasesArray = phaseResponse;
              }
              const phaseRow =
                phasesArray.find(
                  (p) =>
                    p.projectPhaseId == phaseId ||
                    p.id == phaseId ||
                    p.projectPhase?.projectPhaseId == phaseId
                ) || phasesArray[0];

              if (phaseRow) {
                let projectData = phaseRow.project || phaseRow.projectPhase?.project || null;
                const projectIdFromPhase =
                  phaseRow.projectId ??
                  phaseRow.projectPhase?.projectId ??
                  projectData?.projectId ??
                  projectId;

                if (!projectData && projectIdFromPhase) {
                  const projectResponse = await axiosInstance.get(`/projects`, {
                    params: { projectId: projectIdFromPhase, customerId: null },
                  });
                  const items =
                    projectResponse?.items ||
                    (Array.isArray(projectResponse) ? projectResponse : []);
                  projectData = items[0] || null;
                }

                phaseDataItem = buildPhaseDataItemFromProjectPhase(phaseRow, projectData);
              }
            }
          } catch (fetchError) {
            console.error('Error fetching phase data:', fetchError);
            if (fetchError.response?.status === 404 || fetchError.response?.status >= 500) {
              navigate('/interior-designer/phases');
              return;
            }
            toast.error('Failed to load phase details');
            navigate('/interior-designer/phases');
            return;
          }
        }

        if (!phaseDataItem || !phaseDataItem.projectPhase) {
          // Only show error and navigate if component is still mounted
          if (isMounted && !passedPhaseData) {
            console.warn('Phase not found in response');
            toast.error('Phase not found');
            navigate('/interior-designer/phases');
          }
          return;
        }

        // Don't proceed if component unmounted
        if (!isMounted) return;

        // Extract all data directly from phase data (either passed or fetched)
        const projectPhase = phaseDataItem.projectPhase || {};
        const projectData = projectPhase.project || {};
        let buildingsArray = phaseDataItem.buildings || [];
        let configsArray =
          phaseDataItem.configs ||
          phaseDataItem.configurations ||
          phaseDataItem.propertyConfigurations ||
          [];
        const completionPercent = phaseDataItem.completionPercentage ?? 0;

        const resolvedProjectId =
          projectData?.projectId ??
          projectPhase?.projectId ??
          (typeof projectId !== 'undefined' ? projectId : null);

        // Phase payloads often omit buildings (same as BV_PhaseDetails / BuildingsAndConfigs)
        if ((!buildingsArray || buildingsArray.length === 0) && phaseId && resolvedProjectId) {
          try {
            const bParams = { projectPhaseId: phaseId };
            const custId = userInfo?.loginIdentifierDetails?.customer?.customerId;
            if (custId) bParams.customerId = custId;
            const bResp = await axiosInstance.get('/projects/buildings', { params: bParams });
            let bList = [];
            if (bResp?.items && Array.isArray(bResp.items)) bList = bResp.items;
            else if (Array.isArray(bResp)) bList = bResp;
            if (bList.length > 0) buildingsArray = bList;
          } catch (bErr) {
            console.error('Error fetching buildings for phase:', bErr);
          }
        }

        // /projects/phase rows often omit nested configs; load via /property/config
        if ((!configsArray || configsArray.length === 0) && resolvedProjectId && phaseId) {
          try {
            const params = {
              projectId: resolvedProjectId,
              projectPhaseId: phaseId,
            };
            const customerId = userInfo?.loginIdentifierDetails?.customer?.customerId;
            if (customerId) {
              params.customerId = customerId;
            }
            const configResponse = await fetchPropertyConfigurations(params);
            let list = [];
            if (configResponse?.items && Array.isArray(configResponse.items)) {
              list = configResponse.items;
            } else if (Array.isArray(configResponse)) {
              list = configResponse;
            }
            configsArray = list;
          } catch (cfgErr) {
            console.error('Error fetching property configurations for phase:', cfgErr);
          }
        }

        // Set all data from phase data
        setPhase(projectPhase);
        setProject(projectData);
        setBuildings(buildingsArray);
        setPhaseCompletionPercent(completionPercent);
        setConfigurations(configsArray);

        // Set subscription fee from phase data
        setSubscriptionFee(phaseDataItem.subscriptionFee ?? 0);

        // Note: Subscription status will be checked separately via subscriptions API

        // Calculate property counts directly from configs (using propertiesId array)
        const counts = {};
        configsArray.forEach((config) => {
          const configId = config.propertyConfigId || config.id;
          // Use propertiesId array length from config data (or 0 if null)
          counts[configId] = Array.isArray(config.propertiesId) ? config.propertiesId.length : 0;
        });
        setPropertyCounts(counts);

        // Step 2: Fetch phase image (only if not cached and not in response)
        const cacheKey = `phase_${phaseId}`;
        const cachedPhaseImage = getCachedImage(cacheKey);

        if (cachedPhaseImage) {
          setPhaseImage(cachedPhaseImage);
        } else {
          // Fetch phase image if not cached
          try {
            const phaseImageResponse = await axiosInstance.get('/projects/phase/media', {
              params: { projectPhaseId: phaseId, images: true, progress: false, guidelines: false, boq: false }
            }).catch(() => ({ items: [], data: [] }));

            let imagesArray = [];
            if (phaseImageResponse?.items && Array.isArray(phaseImageResponse.items)) {
              imagesArray = phaseImageResponse.items;
            } else if (Array.isArray(phaseImageResponse)) {
              imagesArray = phaseImageResponse;
            }

            if (imagesArray.length > 0) {
              const [mediaData] = mapMediaItems(imagesArray, 'Phase Image');
              setPhaseImage(mediaData);
              if (mediaData) {
                setCachedImage(cacheKey, mediaData);
              }
            }
          } catch (error) {
            console.error('Error processing phase images:', error);
          }
        }

        // Step 3: Fetch config images only (property counts already available from configs)
        if (configsArray.length > 0) {
          // Check cache for config images first
          const imagesMap = {};
          const configsToFetch = [];

          configsArray.forEach((config) => {
            const configId = config.propertyConfigId || config.id;
            const cacheKey = `config_${configId}`;
            const cachedConfigImage = getCachedImage(cacheKey);
            if (cachedConfigImage && !isDetailedFloorPlanMedia(cachedConfigImage)) {
              imagesMap[configId] = cachedConfigImage;
            } else {
              configsToFetch.push(config);
            }
          });

          // Fetch config images in parallel (only for non-cached ones)
          if (configsToFetch.length > 0) {
            const configImagePromises = configsToFetch.map(async (config) => {
              const configId = config.propertyConfigId || config.id;
              const cacheKey = `config_${configId}`;

              try {
                const mediaParams = { property_config_id: configId };
                const customerId = userInfo?.loginIdentifierDetails?.customer?.customerId;
                if (customerId) {
                  mediaParams.customerId = customerId;
                }
                const configImagesResponse = await axiosInstance.get('/property/config/media', {
                  params: mediaParams,
                });

                const { gallery } = normalizePropertyConfigMediaForUI(configImagesResponse);
                const mediaData = gallery[0] || null;
                if (mediaData) {
                  setCachedImage(cacheKey, mediaData);
                  return { configId, image: mediaData };
                }
              } catch (error) {
                console.error(`Error fetching images for config ${configId}:`, error);
              }
              return { configId, image: null };
            });

            const imageResults = await Promise.all(configImagePromises);

            // Merge fetched images with cached ones
            imageResults.forEach(({ configId, image }) => {
              if (image) {
                imagesMap[configId] = image;
              }
            });
          }

          setConfigImages(imagesMap);
        }

        try {
          const mediaData = await fetchProjectPhaseMedia(phaseId, {
            images: false,
            progress: false,
            guidelines: true,
            boq: true,
          });
          const mediaArray = Array.isArray(mediaData) ? mediaData : mediaData?.items || [];
          const organized = organizePhaseDocumentsFromMedia(mediaArray);
          if (isMounted) setPhaseMedia(organized);
        } catch (docErr) {
          console.error('Error fetching phase documents:', docErr);
          if (isMounted) setPhaseMedia({ images: [], guidelines: [], boq: [] });
        }

      } catch (error) {
        console.error('Error fetching phase details:', error);
        if (isMounted) {
          toast.error('Failed to load phase details');
          navigate('/interior-designer/phases');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (projectId && phaseId) {
      fetchPhaseDetails();
    }

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, phaseId, userInfo]); // userInfo: customerId for /property/config when embedded configs are missing

  // Check subscription status from subscriptions API
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      if (!phaseId || !userInfo?.loginId) return;

      try {
        const response = await axiosInstance.get(`/interior-designers/phase-subscriptions`);

        let subscriptions = [];
        if (response?.items && Array.isArray(response.items)) {
          subscriptions = response.items;
        } else if (Array.isArray(response)) {
          subscriptions = response;
        }

        // Find subscription for this phase
        const phaseSubscription = subscriptions.find(
          sub => sub.projectPhase?.projectPhaseId == phaseId || sub.projectPhaseId == phaseId
        );

        if (phaseSubscription) {
          const paymentStatus = phaseSubscription.paymentStatus?.toUpperCase();
          if (paymentStatus === 'PAID') {
            setSubscriptionStatus('paid');
          } else if (paymentStatus === 'PENDING') {
            setSubscriptionStatus('pending');
          } else {
            setSubscriptionStatus('none');
          }
        } else {
          setSubscriptionStatus('none');
        }
      } catch (error) {
        console.error('Error checking subscription status:', error);
        setSubscriptionStatus('none');
      }
    };

    if (phaseId && userInfo?.loginId) {
      checkSubscriptionStatus();
    }
  }, [phaseId, userInfo]);

  // Building metadata — `progressPercentCompletion` lives on /building/metadata,
  // not on /projects/buildings. We fetch it separately so the completion-%
  // fallback chain has real values to average.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!phaseId) return;
      try {
        const resp = await axiosInstance.get('/building/metadata', {
          params: { projectPhaseId: phaseId },
        });
        let list = [];
        if (resp?.items && Array.isArray(resp.items)) list = resp.items;
        else if (Array.isArray(resp)) list = resp;
        if (!cancelled) setBuildingMetadata(list);
      } catch (err) {
        console.error('Error fetching building metadata for phaseId', phaseId, err);
        if (!cancelled) setBuildingMetadata([]);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [phaseId]);

  useEffect(() => {
    if (!boqModalOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setBoqModalOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [boqModalOpen]);

  const handleBackClick = () => {
    navigate('/interior-designer/phases');
  };

  const handleConfigClick = (config) => {
    const configId = config.propertyConfigId || config.id;
    if (projectId && configId) {
      // Pass config data and project data via navigation state to avoid refetching
      navigate(`/interior-designer/phases/${projectId}/configs/${configId}`, {
        state: {
          configData: config,
          projectData: project,
          phaseId: phaseId
        }
      });
    }
  };

  const handleUnlockClick = () => {
    setShowUnlockModal(true);
  };

  const handleViewBoqClick = () => {
    if (subscriptionStatus !== 'paid') {
      handleUnlockClick();
      return;
    }
    setBoqModalOpen(true);
  };

  const handleCloseModal = () => {
    if (!subscriptionLoading) {
      setShowUnlockModal(false);
    }
  };

  const handleConfirmSubscribe = async () => {
    if (!phaseId) {
      toast.error('Phase ID is missing');
      return;
    }

    setSubscriptionLoading(true);
    try {
      // Use subscription fee from state (set from comprehensive endpoint)
      const feeToUse = subscriptionFee > 0 ? subscriptionFee : 5000; // Fallback to default if not set

      await axiosInstance.post('/interior-designers/phase-subscriptions', {
        projectPhaseId: parseInt(phaseId),
        subscriptionFee: feeToUse,
        isAvailableOnSite: availableOnSite
      });

      toast.success('Subscription request submitted successfully! Payment approval is pending.');
      setShowUnlockModal(false);

      // Refresh subscription status immediately
      try {
        const subResponse = await axiosInstance.get(`/interior-designers/phase-subscriptions`);
        let subscriptions = [];
        if (subResponse?.items && Array.isArray(subResponse.items)) {
          subscriptions = subResponse.items;
        } else if (Array.isArray(subResponse)) {
          subscriptions = subResponse;
        }

        const phaseSubscription = subscriptions.find(
          sub => sub.projectPhase?.projectPhaseId == phaseId || sub.projectPhaseId == phaseId
        );

        if (phaseSubscription) {
          const paymentStatus = phaseSubscription.paymentStatus?.toUpperCase();
          if (paymentStatus === 'PAID') {
            setSubscriptionStatus('paid');
          } else if (paymentStatus === 'PENDING') {
            setSubscriptionStatus('pending');
          } else {
            setSubscriptionStatus('none');
          }
        } else {
          setSubscriptionStatus('none');
        }
      } catch (error) {
        console.error('Error refreshing subscription:', error);
        setSubscriptionStatus('pending'); // Assume pending if refresh fails
      }
    } catch (error) {
      console.error('Error subscribing to phase:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to subscribe to phase. Please try again.');
      }
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // Subscription status pill — matches the Phases.jsx card overlay style.
  const renderSubscriptionPill = (status) => {
    if (status === 'paid') {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/95 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm backdrop-blur-sm">
          <CheckCircle size={12} />
          Unlocked
        </span>
      );
    }
    if (status === 'pending') {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/95 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm backdrop-blur-sm">
          <Clock size={12} />
          Pending
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-slate-900/80 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm backdrop-blur-sm">
        <Lock size={12} />
        Locked
      </span>
    );
  };

  // Clean skeleton loader using the slate visual language.
  const SkeletonLoader = () => (
    <div className="px-4 sm:px-6 py-6 space-y-6">
      <div className="h-4 w-32 animate-pulse rounded bg-slate-100" />
      <div className="h-48 animate-pulse rounded-2xl bg-slate-100" />
      <div className="h-12 w-72 animate-pulse rounded-2xl bg-slate-100" />
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-24 animate-pulse rounded-2xl bg-slate-100" />
        ))}
      </div>
      <div className="h-40 animate-pulse rounded-2xl bg-slate-100" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-64 animate-pulse rounded-2xl bg-slate-100" />
        ))}
      </div>
    </div>
  );

  if (loading) {
    return <SkeletonLoader />;
  }

  if (!phase || !project) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center rounded-2xl border border-slate-200 bg-white p-10 shadow-sm">
          <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
            <Building2 className="text-slate-400" size={24} />
          </div>
          <p className="mt-4 text-base font-medium text-slate-800">Phase not found</p>
          <p className="mt-1 text-sm text-slate-500">The phase you’re looking for is unavailable.</p>
          <OutlineButton onClick={handleBackClick} className="mt-4">
            Back to Phases
          </OutlineButton>
        </div>
      </div>
    );
  }

  // Completion percentage — try, in order:
  // 1. Server-supplied phaseDataItem.completionPercentage
  // 2. phase.progressPercentCompletion (older shape)
  // 3. Average of buildings' progressPercentCompletion (the underlying status
  //    data the user expects to drive this number when the rolled-up field
  //    isn't populated by the BE yet)
  // Average across /building/metadata entries first; fall back to the
  // /projects/buildings list shape if metadata didn't return anything.
  const buildingsAvgCompletion = (() => {
    const sources = [
      Array.isArray(buildingMetadata) ? buildingMetadata : [],
      Array.isArray(buildings) ? buildings : [],
    ];
    for (const src of sources) {
      const vals = src
        .map((b) => parseFloat(b?.progressPercentCompletion))
        .filter((n) => Number.isFinite(n) && n > 0);
      if (vals.length > 0) {
        return vals.reduce((a, b) => a + b, 0) / vals.length;
      }
    }
    return 0;
  })();
  const completionPercent = (() => {
    const fromServer = parseFloat(phaseCompletionPercent);
    if (Number.isFinite(fromServer) && fromServer > 0) return fromServer;
    const fromPhase = parseFloat(phase.progressPercentCompletion);
    if (Number.isFinite(fromPhase) && fromPhase > 0) return fromPhase;
    return buildingsAvgCompletion;
  })();
  const cityState = project.address
    ? `${project.address.city || ''}, ${project.address.state || ''}`.replace(/^,\s*|,\s*$/g, '')
    : 'Location not available';
  const fullAddress = (() => {
    if (!project.address) return cityState;
    const a = project.address;
    const parts = [
      [a.buildingNo, a.streetName1].filter(Boolean).join(' '),
      a.streetName2,
      a.district,
      a.city,
      a.state,
      a.pinCode,
    ].filter((s) => s && String(s).trim() !== '');
    return parts.length ? parts.join(', ') : cityState;
  })();
  const docsLocked = subscriptionStatus !== 'paid';

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  // Stat tiles shown in Overview.
  const statTiles = [
    {
      label: 'Configurations',
      value: configurations.length,
      icon: Layers,
    },
    {
      label: 'Buildings',
      value: buildings.length,
      icon: Building2,
    },
    {
      label: 'Total properties',
      value: Object.values(propertyCounts).reduce((sum, n) => sum + (n || 0), 0),
      icon: Home,
    },
    {
      label: 'Towers',
      value:
        phase.towers !== null && phase.towers !== undefined && phase.towers !== ''
          ? phase.towers
          : '—',
      icon: ShieldCheck,
    },
  ];

  // Tab metadata — drives the pill nav.
  const TABS = [
    { id: 'overview', label: 'Overview', icon: Info },
    { id: 'configs', label: 'Configurations', icon: Layers, count: configurations.length },
    {
      id: 'documents',
      label: 'Documents',
      icon: FileText,
      count: phaseMedia.guidelines.length + phaseMedia.boq.length,
    },
    { id: 'boq', label: 'Developer BOQ', icon: ImageIcon },
  ];

  return (
    <div className="px-4 sm:px-6 py-6 space-y-6">
      {/* Back link above the hero */}
      <button
        type="button"
        onClick={handleBackClick}
        className="inline-flex items-center gap-1 text-sm text-slate-500 transition-colors hover:text-flamingo-600"
      >
        <ArrowLeft size={14} />
        Back to phases
      </button>

      {/* Hero band — slate gradient with radial overlay, status row + completion pill */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800 to-slate-600 shadow-sm">
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 0%, rgba(255,255,255,0.6) 0, transparent 50%), radial-gradient(circle at 80% 100%, rgba(255,255,255,0.4) 0, transparent 50%)',
          }}
        />
        {phaseImage && (
          <div className="pointer-events-none absolute inset-0 opacity-25">
            <LoadingImage
              src={getMediaUrl(phaseImage)}
              alt={phase.name || 'Phase'}
              containerClassName="w-full h-full"
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/40 via-slate-800/30 to-transparent" />

        <div className="relative px-6 py-7 sm:px-8 sm:py-8">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-white/80 backdrop-blur-sm">
            <Sparkles size={12} />
            Interior Designer · Phase
          </div>

          <h1 className="mt-3 text-2xl sm:text-3xl font-semibold text-white">
            {phase.name || 'Unnamed Phase'}
          </h1>

          {(project.projectName || project.developer) && (
            <p className="mt-1 text-sm text-white/80">
              {project.projectName || project.developer}
              {project.projectName && project.developer ? (
                <span className="text-white/60"> · {project.developer}</span>
              ) : null}
            </p>
          )}

          <div className="mt-2 flex items-start gap-1.5 text-xs text-white/70">
            <MapPin size={13} className="mt-0.5 shrink-0" />
            <span className="line-clamp-2">{fullAddress}</span>
          </div>

          {/* Status row: subscription pill + completion pill */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {renderSubscriptionPill(subscriptionStatus)}
            <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-flamingo-500 to-flamingo-600 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm">
              {completionPercent.toFixed(2)}% complete
            </span>
            {phase.reraNo && (
              <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-medium text-white/85 backdrop-blur-sm ring-1 ring-white/10">
                RERA · {phase.reraNo}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Subscription banner */}
      {subscriptionStatus === 'none' && (
        <div className="rounded-2xl border border-flamingo-200 bg-gradient-to-r from-amber-50 to-flamingo-50 p-4 sm:p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-flamingo-500 text-white">
                <Lock size={18} />
              </div>
              <div>
                <h2 className="text-base font-semibold text-slate-900">Unlock this phase</h2>
                <p className="mt-0.5 text-sm text-slate-600">
                  Subscribe to access all configurations, AutoCAD plans, BOQ docs and the ability to upload your own designs.
                </p>
              </div>
            </div>
            <PrimaryButton
              onClick={handleUnlockClick}
              disabled={subscriptionLoading}
              className="!w-auto !px-4 !py-2 text-sm whitespace-nowrap"
            >
              <span className="inline-flex items-center gap-1.5">
                <Sparkles size={14} />
                Subscribe to phase
              </span>
            </PrimaryButton>
          </div>
        </div>
      )}

      {subscriptionStatus === 'pending' && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 sm:p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-500 text-white">
                <Clock size={18} />
              </div>
              <div>
                <h2 className="text-base font-semibold text-slate-900">Subscription pending</h2>
                <p className="mt-0.5 text-sm text-slate-600">
                  Your subscription request is awaiting approval. You'll be notified when it's active.
                </p>
              </div>
            </div>
            <OutlineButton
              onClick={handleUnlockClick}
              disabled={subscriptionLoading}
              className="!w-auto !px-4 !py-2 text-sm whitespace-nowrap"
            >
              View details
            </OutlineButton>
          </div>
        </div>
      )}

      {subscriptionStatus === 'paid' && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500 text-white">
              <CheckCircle size={18} />
            </div>
            <div>
              <h2 className="text-base font-semibold text-slate-900">Phase unlocked</h2>
              <p className="mt-0.5 text-sm text-slate-600">
                You have full access to all configurations, plans and documents in this phase.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Pill tab nav */}
      <div className="inline-flex flex-wrap gap-1 rounded-2xl border border-slate-200 bg-white p-1.5 shadow-sm">
        {TABS.map(({ id, label, icon: Icon, count }) => {
          const active = activeTab === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => selectTab(id)}
              className={`inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
                active
                  ? 'bg-gradient-to-r from-flamingo-500 to-flamingo-600 text-white shadow-sm'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              }`}
            >
              <Icon size={14} />
              {label}
              {typeof count === 'number' && count > 0 && (
                <span
                  className={`tabular-nums rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${
                    active ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab panels */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Stat tiles */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <div className="mb-5">
              <h2 className="text-lg font-semibold text-slate-900">At a glance</h2>
              <p className="mt-0.5 text-sm text-slate-500">
                Quick snapshot of what this phase contains.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {statTiles.map(({ label, value, icon: Icon }) => (
                <div key={label} className="rounded-xl border border-slate-100 bg-slate-50/40 p-4">
                  <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
                    <Icon size={13} className="text-slate-400" />
                    {label}
                  </div>
                  <div className="mt-1.5 text-2xl font-semibold text-slate-900 tabular-nums">
                    {value}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* About + completion */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8 lg:col-span-2">
              <h2 className="text-lg font-semibold text-slate-900">About the project</h2>
              <p className="mt-0.5 text-sm text-slate-500">
                {project.developer ? `${project.developer} · ` : ''}
                {phase.name || 'This phase'}
              </p>

              {project.description?.trim() ? (
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  {project.description.trim()}
                </p>
              ) : (
                <p className="mt-4 text-sm italic text-slate-400">No description provided.</p>
              )}

              <div className="mt-6 grid grid-cols-1 gap-4 border-t border-slate-100 pt-5 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Location
                  </p>
                  <p className="mt-1 text-sm text-slate-900">{cityState}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Project type
                  </p>
                  <p className="mt-1 text-sm text-slate-900">
                    {project.type != null && String(project.type).trim() !== '' ? project.type : '—'}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
              <h2 className="text-lg font-semibold text-slate-900">Phase completion</h2>
              <p className="mt-0.5 text-sm text-slate-500">Overall progress for this phase</p>

              <div className="mt-5 flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-flamingo-500 to-flamingo-600 transition-[width] duration-300"
                      style={{ width: `${Math.min(100, Math.max(0, completionPercent))}%` }}
                    />
                  </div>
                </div>
                <span className="shrink-0 text-2xl font-semibold text-slate-900 tabular-nums">
                  {completionPercent.toFixed(2)}%
                </span>
              </div>

              <div className="mt-6 space-y-3 border-t border-slate-100 pt-5 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Unit configurations</span>
                  <span className="font-medium text-slate-900">{configurations.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Buildings</span>
                  <span className="font-medium text-slate-900">{buildings.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Subscription fee</span>
                  <span className="font-medium text-slate-900">
                    {subscriptionFee > 0 ? `₹${Number(subscriptionFee).toLocaleString('en-IN')}` : '—'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* RERA & inventory */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <h2 className="text-lg font-semibold text-slate-900">RERA &amp; inventory</h2>
            <p className="mt-0.5 text-sm text-slate-500">
              Regulatory registration and unit summary.
            </p>
            <dl className="mt-5 grid grid-cols-1 gap-x-6 gap-y-4 text-sm sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  RERA registration
                </dt>
                <dd className="mt-1 font-medium text-slate-900">
                  {phase.reraNo != null && String(phase.reraNo).trim() !== ''
                    ? String(phase.reraNo).trim()
                    : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Towers</dt>
                <dd className="mt-1 font-medium text-slate-900">
                  {phase.towers !== null && phase.towers !== undefined && phase.towers !== ''
                    ? phase.towers
                    : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Units</dt>
                <dd className="mt-1 font-medium text-slate-900">
                  {phase.units !== null && phase.units !== undefined && phase.units !== ''
                    ? phase.units
                    : '—'}
                </dd>
              </div>
              {(phase.reraStartDate || phase.reraEndDate) && (
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    RERA period
                  </dt>
                  <dd className="mt-1 font-medium text-slate-900">
                    {phase.reraStartDate ? formatDate(phase.reraStartDate) : '—'}
                    {' '}
                    <span className="text-slate-400">–</span>
                    {' '}
                    {phase.reraEndDate ? formatDate(phase.reraEndDate) : '—'}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Buildings */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <h2 className="text-lg font-semibold text-slate-900">Buildings</h2>
            <p className="mt-0.5 text-sm text-slate-500">
              {buildings.length === 0
                ? 'No buildings listed for this phase.'
                : `${buildings.length} ${buildings.length === 1 ? 'building' : 'buildings'} in this phase`}
            </p>

            {buildings.length === 0 ? (
              <div className="mt-5 rounded-xl border border-dashed border-slate-200 bg-slate-50/40 p-6 text-center">
                <div className="mx-auto inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-100">
                  <Building2 className="text-slate-400" size={18} />
                </div>
                <p className="mt-2 text-sm text-slate-500">None listed for this phase.</p>
              </div>
            ) : (
              <ul className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {buildings.map((building) => {
                  const buildingId = building.buildingId || building.id;
                  const buildingName = building.buildingName || building.name || 'Unnamed building';
                  const floors = building.numberOfFloors ?? building.floors;
                  const floorText =
                    floors != null && floors !== ''
                      ? `${floors} ${Number(floors) === 1 ? 'floor' : 'floors'}`
                      : null;
                  return (
                    <li
                      key={buildingId ?? buildingName}
                      className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/40 p-3"
                    >
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-slate-500 ring-1 ring-slate-200">
                        <Building2 size={16} />
                      </div>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-slate-900">
                          {buildingName}
                        </p>
                        {floorText && (
                          <p className="text-xs text-slate-500">{floorText}</p>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}

      {activeTab === 'configs' && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="mb-5 flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Available configurations</h2>
              <p className="mt-0.5 text-sm text-slate-500">
                Floor plans and unit layouts available in this phase.
              </p>
            </div>
            {configurations.length > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                <Layers size={12} />
                {configurations.length} {configurations.length === 1 ? 'config' : 'configs'}
              </span>
            )}
          </div>

          {configurations.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/40 py-12 text-center">
              <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                <Building2 className="text-slate-400" size={22} />
              </div>
              <p className="mt-3 text-base font-medium text-slate-800">No configurations available</p>
              <p className="mt-1 text-sm text-slate-500">
                Configurations will appear here when added to this phase.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {configurations.map((config) => {
                // Use same configId extraction as ConfigDetails
                const configId = config.propertyConfigId || config.id;
                const configImage = configImages[configId];
                const configName = config.configName || config.propertyConfig?.configName || 'Unnamed Config';
                const isLocked = config.isLocked || false;
                const propertyCount = propertyCounts[configId] !== undefined ? propertyCounts[configId] : 0;
                const fileCount = config.images?.length || (configImage ? 1 : 0);

                return (
                  <div
                    key={configId}
                    onClick={() => handleConfigClick(config)}
                    className="group flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md"
                  >
                    <div className="relative overflow-hidden">
                      {configImage ? (
                        <LoadingImage
                          src={getMediaUrl(configImage)}
                          alt={configName}
                          containerClassName="h-40 w-full"
                          className="h-40 w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                      ) : (
                        <div className="flex h-40 w-full items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
                          <Building2 className="h-10 w-10 text-slate-300" />
                        </div>
                      )}

                      {isLocked && (
                        <div className="pointer-events-none absolute left-2.5 top-2.5">
                          <span className="inline-flex items-center gap-1 rounded-full bg-slate-900/80 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm backdrop-blur-sm">
                            <Lock size={12} />
                            Locked
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-1 flex-col p-4">
                      <h3 className="truncate text-base font-semibold text-slate-900" title={configName}>
                        {configName}
                      </h3>

                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        <span className="inline-flex items-center rounded-full bg-flamingo-50 px-2.5 py-0.5 text-[11px] font-medium text-flamingo-700">
                          {propertyCount} {propertyCount === 1 ? 'property' : 'properties'}
                        </span>
                        {fileCount > 0 && (
                          <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-medium text-slate-700">
                            {fileCount} {fileCount === 1 ? 'file' : 'files'}
                          </span>
                        )}
                      </div>

                      <div className="mt-auto pt-4">
                        <div className="flex items-center justify-between border-t border-slate-100 pt-3">
                          <span className="text-xs font-medium text-slate-500">View configuration</span>
                          <ChevronRight
                            size={14}
                            className="text-slate-400 transition-all group-hover:translate-x-0.5 group-hover:text-flamingo-500"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'documents' && (
        <div className="space-y-6">
          {/* Guidelines */}
          <div className="relative rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <div className="mb-5 flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Interior design guidelines</h2>
                <p className="mt-0.5 text-sm text-slate-500">
                  {phaseMedia.guidelines.length === 0
                    ? 'No guideline files uploaded yet.'
                    : `${phaseMedia.guidelines.length} ${phaseMedia.guidelines.length === 1 ? 'file' : 'files'}`}
                </p>
              </div>
              {phaseMedia.guidelines.length > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                  <FileText size={12} />
                  {phaseMedia.guidelines.length}
                </span>
              )}
            </div>

            {phaseMedia.guidelines.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/40 py-10 text-center">
                <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                  <FileText className="text-slate-400" size={22} />
                </div>
                <p className="mt-3 text-sm text-slate-500">No guideline files uploaded.</p>
              </div>
            ) : (
              <ul className="space-y-2">
                {phaseMedia.guidelines.map((doc, docIndex) => (
                  <PhaseDocumentFileRow
                    key={doc.key || doc.id || doc.mediaId || `g-${docIndex}`}
                    doc={doc}
                    docIndex={docIndex}
                    defaultLabel="Guideline"
                    compact
                    disabled={docsLocked}
                    onLockedClick={handleUnlockClick}
                  />
                ))}
              </ul>
            )}

            {docsLocked && phaseMedia.guidelines.length > 0 && (
              <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-white/75 backdrop-blur-[2px]">
                <div className="pointer-events-auto flex max-w-xs flex-col items-center rounded-2xl border border-slate-200 bg-white/95 px-5 py-5 text-center shadow-sm">
                  <div className="mx-auto inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-100">
                    <Lock className="text-slate-500" size={18} aria-hidden />
                  </div>
                  <p className="mt-3 text-sm font-semibold text-slate-900">Documents locked</p>
                  <p className="mt-1 text-xs text-slate-500">
                    Subscribe to download guideline files.
                  </p>
                  <PrimaryButton
                    type="button"
                    onClick={handleUnlockClick}
                    disabled={subscriptionLoading}
                    className="!mt-3 !w-auto !px-3 !py-1.5 text-xs"
                  >
                    <span className="inline-flex items-center gap-1.5">
                      <Sparkles size={12} />
                      Subscribe
                    </span>
                  </PrimaryButton>
                </div>
              </div>
            )}
          </div>

          {/* RERA BOQ docs */}
          <div className="relative rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <div className="mb-5 flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">RERA BOQ documents</h2>
                <p className="mt-0.5 text-sm text-slate-500">
                  {phaseMedia.boq.length === 0
                    ? 'No BOQ files uploaded yet.'
                    : `${phaseMedia.boq.length} ${phaseMedia.boq.length === 1 ? 'file' : 'files'}`}
                </p>
              </div>
              {phaseMedia.boq.length > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                  <FileText size={12} />
                  {phaseMedia.boq.length}
                </span>
              )}
            </div>

            {phaseMedia.boq.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/40 py-10 text-center">
                <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                  <FileText className="text-slate-400" size={22} />
                </div>
                <p className="mt-3 text-sm text-slate-500">No BOQ documents uploaded.</p>
              </div>
            ) : (
              <ul className="space-y-2">
                {phaseMedia.boq.map((doc, docIndex) => (
                  <PhaseDocumentFileRow
                    key={doc.key || doc.id || doc.mediaId || `b-${docIndex}`}
                    doc={doc}
                    docIndex={docIndex}
                    defaultLabel="RERA BOQ"
                    rightAction="view"
                    compact
                    disabled={docsLocked}
                    onLockedClick={handleUnlockClick}
                  />
                ))}
              </ul>
            )}

            {docsLocked && phaseMedia.boq.length > 0 && (
              <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-white/75 backdrop-blur-[2px]">
                <div className="pointer-events-auto flex max-w-xs flex-col items-center rounded-2xl border border-slate-200 bg-white/95 px-5 py-5 text-center shadow-sm">
                  <div className="mx-auto inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-100">
                    <Lock className="text-slate-500" size={18} aria-hidden />
                  </div>
                  <p className="mt-3 text-sm font-semibold text-slate-900">Documents locked</p>
                  <p className="mt-1 text-xs text-slate-500">
                    Subscribe to download BOQ files.
                  </p>
                  <PrimaryButton
                    type="button"
                    onClick={handleUnlockClick}
                    disabled={subscriptionLoading}
                    className="!mt-3 !w-auto !px-3 !py-1.5 text-xs"
                  >
                    <span className="inline-flex items-center gap-1.5">
                      <Sparkles size={12} />
                      Subscribe
                    </span>
                  </PrimaryButton>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'boq' && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Developer project BOQ</h2>
              <p className="mt-0.5 text-sm text-slate-500">
                View the developer&rsquo;s Bill of Quantities for this phase.
              </p>
            </div>
            <PrimaryButton
              type="button"
              onClick={handleViewBoqClick}
              className="!w-auto !px-4 !py-2 text-sm whitespace-nowrap"
            >
              <span className="inline-flex items-center gap-1.5">
                <Eye size={14} />
                {subscriptionStatus === 'paid' ? 'Open BOQ' : 'Unlock to view'}
              </span>
            </PrimaryButton>
          </div>

          {subscriptionStatus !== 'paid' ? (
            <div className="mt-6 rounded-xl border border-dashed border-slate-200 bg-slate-50/40 py-12 text-center">
              <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                <Lock className="text-slate-400" size={22} />
              </div>
              <p className="mt-3 text-base font-medium text-slate-800">BOQ is locked</p>
              <p className="mt-1 text-sm text-slate-500">
                Subscribe to this phase to view the detailed developer BOQ.
              </p>
              <PrimaryButton
                onClick={handleUnlockClick}
                disabled={subscriptionLoading}
                className="!mt-4 !w-auto !px-4 !py-2 text-sm"
              >
                <span className="inline-flex items-center gap-1.5">
                  <Sparkles size={14} />
                  Subscribe to phase
                </span>
              </PrimaryButton>
            </div>
          ) : (
            <div className="mt-6 rounded-xl border border-slate-100 bg-slate-50/40 p-4">
              <p className="text-sm text-slate-600">
                Click <span className="font-medium text-slate-900">Open BOQ</span> above to view the
                developer&rsquo;s Bill of Quantities in a dedicated viewer.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Developer BOQ modal (unchanged behavior) */}
      {boqModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="id-boq-modal-title"
          onClick={() => setBoqModalOpen(false)}
        >
          <div
            className="flex w-full max-w-5xl max-h-[90vh] flex-col rounded-2xl border border-slate-200 bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-200 px-6 py-4">
              <div>
                <h3 id="id-boq-modal-title" className="text-lg font-semibold text-slate-900">
                  Developer project BOQ
                </h3>
                <p className="mt-0.5 text-xs text-slate-500">
                  {phase.name || 'This phase'}
                  {project.projectName ? ` · ${project.projectName}` : ''}
                </p>
              </div>
              <CloseButton
                type="button"
                onClick={() => setBoqModalOpen(false)}
                aria-label="Close dialog"
                className="shrink-0 border-slate-300"
              />
            </div>
            <div className="min-h-0 flex-1 overflow-auto p-6">
              <DeveloperProjectBoqTable
                projectPhaseId={phaseId}
                readOnly
                showTitle={false}
                title=""
                className="p-0"
              />
            </div>
          </div>
        </div>
      )}

      {/* Unlock Phase Confirmation Modal */}
      {showUnlockModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm">
          <div className="relative flex w-full max-w-2xl max-h-[90vh] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
            {/* Slate header */}
            <div className="relative shrink-0 overflow-hidden bg-gradient-to-br from-slate-800 to-slate-600 px-6 py-5 text-white">
              <div
                className="absolute inset-0 opacity-[0.08]"
                style={{
                  backgroundImage:
                    'radial-gradient(circle at 20% 0%, rgba(255,255,255,0.6) 0, transparent 50%), radial-gradient(circle at 80% 100%, rgba(255,255,255,0.4) 0, transparent 50%)',
                }}
              />
              <div className="relative flex items-start justify-between gap-3">
                <div>
                  <div className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-0.5 text-[11px] font-medium uppercase tracking-wider text-white/80 backdrop-blur-sm">
                    {subscriptionStatus === 'pending' ? <Clock size={12} /> : <Sparkles size={12} />}
                    {subscriptionStatus === 'pending' ? 'Pending approval' : 'Unlock phase'}
                  </div>
                  <h3 className="mt-2 text-xl font-semibold text-white">
                    {subscriptionStatus === 'pending'
                      ? 'Subscription details'
                      : 'Confirm phase subscription'}
                  </h3>
                  <p className="mt-1 text-sm text-white/70">
                    {subscriptionStatus === 'pending'
                      ? 'Your subscription request is pending approval.'
                      : 'Review the phase details before subscribing.'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  disabled={subscriptionLoading}
                  className="inline-flex items-center justify-center rounded-lg border border-white/20 bg-white/10 p-2 text-white/80 transition-colors hover:bg-white/20 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label="Close"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-6">
              {subscriptionStatus === 'pending' && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-500 text-white">
                      <Clock size={16} />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-slate-900">Subscription pending</h4>
                      <p className="mt-0.5 text-sm text-slate-700">
                        Your subscription request for this phase is currently pending approval.
                        You&rsquo;ll be notified once it&rsquo;s activated.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {subscriptionStatus !== 'pending' && (
                <div className="rounded-xl border border-flamingo-200 bg-gradient-to-r from-amber-50 to-flamingo-50 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xs font-medium uppercase tracking-wide text-slate-600">
                        Subscription fee
                      </span>
                      <p className="mt-1 text-2xl font-semibold text-slate-900">
                        ₹{(subscriptionFee > 0 ? subscriptionFee : 5000).toLocaleString('en-IN')}
                      </p>
                    </div>
                    <span className="text-xs text-slate-500">One-time payment</span>
                  </div>
                </div>
              )}

              <label
                htmlFor="availableOnSite"
                className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-white p-4"
              >
                <input
                  id="availableOnSite"
                  type="checkbox"
                  checked={availableOnSite}
                  onChange={(e) => setAvailableOnSite(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-flamingo-600 focus:ring-flamingo-500"
                />
                <span>
                  <span className="block text-sm font-semibold text-slate-900">
                    I'll be available on-site for this project
                  </span>
                  <span className="mt-0.5 block text-xs text-slate-500">
                    Buyers and developers will see an "Available on-site" tag on your designs for
                    this phase, indicating you can visit in person.
                  </span>
                </span>
              </label>

              <div className="rounded-xl border border-slate-100 bg-slate-50/40 p-4">
                <h4 className="font-semibold text-slate-900">Phase information</h4>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      Phase name
                    </span>
                    <p className="mt-1 text-sm text-slate-900">{phase?.name || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      Project
                    </span>
                    <p className="mt-1 text-sm text-slate-900">
                      {project?.projectName || project?.developer || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      Location
                    </span>
                    <p className="mt-1 text-sm text-slate-900">{cityState}</p>
                  </div>
                  <div>
                    <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      Configurations
                    </span>
                    <p className="mt-1 text-sm text-slate-900">{configurations.length} available</p>
                  </div>
                  {phase?.reraNo && (
                    <div>
                      <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        RERA number
                      </span>
                      <p className="mt-1 text-sm text-slate-900">{phase.reraNo}</p>
                    </div>
                  )}
                  {phase?.towers && (
                    <div>
                      <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        Towers
                      </span>
                      <p className="mt-1 text-sm text-slate-900">{phase.towers}</p>
                    </div>
                  )}
                </div>
              </div>

              {subscriptionStatus !== 'pending' && (
                <>
                  <div className="rounded-xl border border-slate-200 bg-white p-4">
                    <h4 className="font-semibold text-slate-900">What you&rsquo;ll get</h4>
                    <ul className="mt-3 space-y-2 text-sm text-slate-700">
                      <li className="flex items-start gap-2">
                        <CheckCircle size={14} className="mt-0.5 shrink-0 text-emerald-500" />
                        <span>
                          Access to all {configurations.length} configuration
                          {configurations.length === 1 ? '' : 's'} in this phase
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle size={14} className="mt-0.5 shrink-0 text-emerald-500" />
                        <span>Download AutoCAD files and detailed plans</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle size={14} className="mt-0.5 shrink-0 text-emerald-500" />
                        <span>Upload your own design submissions</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle size={14} className="mt-0.5 shrink-0 text-emerald-500" />
                        <span>Receive leads from interested buyers</span>
                      </li>
                    </ul>
                  </div>

                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                    <p className="text-sm text-slate-700">
                      <strong className="text-slate-900">Note:</strong> Your subscription request
                      will be reviewed and approved by an administrator. You&rsquo;ll be notified
                      once your subscription is activated.
                    </p>
                  </div>
                </>
              )}
            </div>

            <div className="flex shrink-0 items-center justify-end gap-2 border-t border-slate-200 px-6 py-4">
              {subscriptionStatus === 'pending' ? (
                <PrimaryButton
                  onClick={handleCloseModal}
                  disabled={subscriptionLoading}
                  className="!w-auto !px-4 !py-2 text-sm"
                >
                  Close
                </PrimaryButton>
              ) : (
                <>
                  <OutlineButton
                    onClick={handleCloseModal}
                    disabled={subscriptionLoading}
                    className="!w-auto !px-4 !py-2 text-sm"
                  >
                    Cancel
                  </OutlineButton>
                  <PrimaryButton
                    onClick={handleConfirmSubscribe}
                    disabled={subscriptionLoading}
                    className="!w-auto !px-4 !py-2 text-sm"
                  >
                    {subscriptionLoading ? (
                      <span className="inline-flex items-center gap-2">
                        <svg
                          className="h-4 w-4 animate-spin text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Subscribing…
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5">
                        <Sparkles size={14} />
                        Confirm subscription
                      </span>
                    )}
                  </PrimaryButton>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InteriorDesignerPhaseDetails;
